import React, { ReactNode } from 'react';
import { Dialog, Grid, Stack, Typography, styled, Box, Fade, IconButton, Slide, alpha } from '@mui/material';
import SbButton from '../Button';
import { Close } from '@mui/icons-material';

interface PropsType {
  icon?: string | ReactNode;
  closeIcon?: boolean;
  title?: string;
  subTitle?: string | ReactNode;
  children?: ReactNode;
  open: boolean;
  btnText?: string;
  onClose: () => void;
  onConfirm?: () => void;
  secondaryBtnText?: string;
  format?: boolean;
}

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 700,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  marginTop: theme.spacing(2),
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  fontSize: '1rem',
}));

const DialogContainer = styled(Box)(({ theme }) => ({
  maxWidth: '640px',
  minWidth: '500px',
  borderRadius: '20px',
  overflow: 'hidden',
  boxShadow: theme.shadows[24],
  background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(
    theme.palette.background.default,
    0.95,
  )} 100%)`,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  position: 'relative',
}));

const DialogContent = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(5, 4, 4),
  background: theme.palette.background.paper,
}));

const Header = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  height: '120px',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.common.white, 0.3)}, transparent)`,
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100px',
  height: '100px',
  borderRadius: '50%',
  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.dark, 0.2)}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // border: `4px solid ${theme.palette.common.white}`,
  '& img': {
    width: '56px',
    height: '56px',
    objectFit: 'contain',
  },
  '& svg': {
    fontSize: '48px',
    color: theme.palette.primary.main,
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(2),
  top: theme.spacing(2),
  backgroundColor: alpha(theme.palette.common.white, 0.1),
  backdropFilter: 'blur(4px)',
  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  color: theme.palette.common.white,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.2),
    transform: 'rotate(90deg)',
  },
  '& svg': {
    fontSize: '1.25rem',
  },
}));

// Primary Action Button
const ActionButton = styled(SbButton)(({ theme }) => ({
  height: '48px',
  minWidth: '160px',
  borderRadius: '10px',
  fontWeight: 600,
  fontSize: '0.9375rem',
  textTransform: 'none',
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  border: 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: 'none',
  position: 'relative',
  overflow: 'hidden',

  '&:hover': {
    background: theme.palette.primary.dark,
    transform: 'translateY(-2px)',
    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
  },

  '&:active': {
    transform: 'translateY(0)',
    boxShadow: `0 2px 10px ${alpha(theme.palette.primary.main, 0.2)}`,
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: `linear-gradient(rgba(255, 255, 255, 0.2), transparent)`,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },

  '&:hover::after': {
    opacity: 1,
  },
}));

// Secondary Button (complementary flat design)
const SecondaryButton = styled(ActionButton)(({ theme }) => ({
  background: theme.palette.grey[50],
  color: theme.palette.grey[700],
  borderRadius: '10px',
  boxShadow: 'none',
  border: 'none',
  '&:hover': {
    background: theme.palette.grey[100],
    color: theme.palette.grey[900],
    boxShadow: `0 3px 10px ${alpha(theme.palette.grey[500], 0.2)}`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '0%',
    height: '2px',
    background: theme.palette.primary.main,
    transition: 'width 0.3s ease',
  },
  '&:hover::before': {
    width: '80%',
  },
}));

function Modal({
  open,
  btnText,
  onConfirm,
  onClose,
  icon,
  title,
  subTitle,
  children,
  closeIcon,
  secondaryBtnText,
  format,
}: PropsType) {
  return (
    <Dialog
      maxWidth="lg"
      open={open}
      onClose={onClose}
      scroll={'paper'}
      TransitionComponent={Slide}
      // TransitionProps={{ direction: 'up' }}
      PaperProps={{
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      <Fade in={open} timeout={300}>
        <DialogContainer>
          {closeIcon && (
            <CloseButton onClick={onClose} aria-label="close">
              <Close />
            </CloseButton>
          )}

          <Header>
            <IconContainer>
              {icon && typeof icon === 'string' ? <img src={icon} alt="modal icon" /> : icon}
            </IconContainer>
          </Header>

          <DialogContent
            container
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            textAlign="center"
            spacing={2}
          >
            {children ? (
              <Box width="100%">{children}</Box>
            ) : (
              <>
                {title && (
                  <Title variant="h2" className="font-medium">
                    {title}
                  </Title>
                )}

                {subTitle && (
                  <Subtitle variant="body1" className="mt-2 font-normal">
                    {format ? <span style={{ whiteSpace: 'pre-line' }}>{subTitle}</span> : subTitle}
                  </Subtitle>
                )}

                {(btnText || secondaryBtnText) && (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                    width="100%"
                    mt={4}
                  >
                    {secondaryBtnText && (
                      <SecondaryButton onClick={onClose} variant="contained">
                        {secondaryBtnText}
                      </SecondaryButton>
                    )}

                    {btnText && (
                      <ActionButton onClick={onConfirm} variant="contained" color="primary">
                        {btnText}
                      </ActionButton>
                    )}
                  </Stack>
                )}
              </>
            )}
          </DialogContent>
        </DialogContainer>
      </Fade>
    </Dialog>
  );
}

export default Modal;
